/**
 * Represents a single quiz question parsed from markdown
 */
export interface QuizQuestion {
  /** The raw markdown content of the question (without the answer section) */
  content: string;
  /** All available answer choices for the question */
  choices: string[];
  /** The correct answer(s) for the question */
  correctAnswers: string[];
  /** Whether this is a multiple choice question (true) or single choice (false) */
  isMultipleChoice: boolean;
}

/**
 * The result of parsing a quiz markdown document
 */
export interface ParsedQuiz {
  /** Array of questions extracted from the markdown */
  questions: QuizQuestion[];
  /** Any errors encountered during parsing */
  errors: string[];
}

/**
 * Parses a quiz markdown document into structured questions with answers.
 * 
 * Questions are separated by `---` (three dashes on their own line).
 * Answer choices are specified using:
 * - `:choice:` followed by answer options (use for single-choice questions)
 * - `:choices:` followed by answer options (use for multiple-choice questions)
 * 
 * Mark correct answers by prefixing with `X` instead of `-`:
 * - Regular choice: `- Answer text`
 * - Correct answer: `X Answer text`
 * 
 * @param markdown - The quiz markdown content to parse
 * @returns ParsedQuiz object containing questions and any errors
 * 
 * @example
 * ```
 * const markdown = `
 * # Question 1
 * What is 2+2?
 * :choice:
 * - 3
 * X 4
 * - 5
 * 
 * ---
 * 
 * # Question 2
 * Select all prime numbers:
 * :choices:
 * - 1
 * X 2
 * X 3
 * - 4
 * X 5
 * `;
 * 
 * const parsed = parseQuizMarkdown(markdown);
 * // parsed.questions[0].choices = ['3', '4', '5']
 * // parsed.questions[0].correctAnswers = ['4']
 * // parsed.questions[0].isMultipleChoice = false
 * // parsed.questions[1].choices = ['1', '2', '3', '4', '5']
 * // parsed.questions[1].correctAnswers = ['2', '3', '5']
 * // parsed.questions[1].isMultipleChoice = true
 * ```
 */
export function parseQuizMarkdown(markdown: string): ParsedQuiz {
  const errors: string[] = [];
  const questions: QuizQuestion[] = [];

  // Split by --- separator
  const rawQuestions = markdown.split(/\n---\n/).map(q => q.trim()).filter(q => q.length > 0);

  for (let i = 0; i < rawQuestions.length; i++) {
    const rawQuestion = rawQuestions[i];
    const questionNum = i + 1;

    // Check for :choice: or :choices:
    const singleChoiceMatch = rawQuestion.match(/:choice:\s*\n((?:^[-X]\s+.+$\n?)+)/im);
    const multipleChoiceMatch = rawQuestion.match(/:choices:\s*\n((?:^[-X]\s+.+$\n?)+)/im);

    if (!singleChoiceMatch && !multipleChoiceMatch) {
      errors.push(`Question ${questionNum}: No answer choices found. Use :choice: or :choices: followed by answer options starting with - or X`);
      continue;
    }

    let choices: string[] = [];
    let correctAnswers: string[] = [];
    let isMultipleChoice = false;
    let content = rawQuestion;

    const processChoices = (choiceBlock: string) => {
      const allChoices: string[] = [];
      const correct: string[] = [];

      choiceBlock
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.startsWith('-') || line.startsWith('X'))
        .forEach(line => {
          const isCorrect = line.startsWith('X');
          const answerText = line.substring(1).trim();
          
          if (answerText.length > 0) {
            allChoices.push(answerText);
            if (isCorrect) {
              correct.push(answerText);
            }
          }
        });

      return { choices: allChoices, correctAnswers: correct };
    };

    if (singleChoiceMatch) {
      // Single choice question
      const choiceBlock = singleChoiceMatch[1];
      const processed = processChoices(choiceBlock);
      choices = processed.choices;
      correctAnswers = processed.correctAnswers;

      if (choices.length === 0) {
        errors.push(`Question ${questionNum}: No valid answer choices found after :choice:`);
        continue;
      }

      if (correctAnswers.length === 0) {
        errors.push(`Question ${questionNum}: No correct answer marked. Use X instead of - to mark the correct answer.`);
        continue;
      }

      if (correctAnswers.length > 1) {
        errors.push(`Question ${questionNum}: Single choice question (:choice:) has multiple correct answers. Consider using :choices: instead.`);
      }

      // Remove the answer section from content
      content = rawQuestion.substring(0, singleChoiceMatch.index).trim();
      isMultipleChoice = false;
    } else if (multipleChoiceMatch) {
      // Multiple choice question
      const choiceBlock = multipleChoiceMatch[1];
      const processed = processChoices(choiceBlock);
      choices = processed.choices;
      correctAnswers = processed.correctAnswers;

      if (choices.length === 0) {
        errors.push(`Question ${questionNum}: No valid answer choices found after :choices:`);
        continue;
      }

      if (correctAnswers.length === 0) {
        errors.push(`Question ${questionNum}: No correct answers marked. Use X instead of - to mark correct answers.`);
        continue;
      }

      // Remove the answer section from content
      content = rawQuestion.substring(0, multipleChoiceMatch.index).trim();
      isMultipleChoice = true;
    }

    questions.push({
      content,
      choices,
      correctAnswers,
      isMultipleChoice
    });
  }

  return {
    questions,
    errors
  };
}
